import { useMutation, useQueryClient } from '@tanstack/react-query'
import { produce } from 'immer'
import { contentVersions } from '../../v3/contentVersions'
import type { ReadContentVersionsDocument } from '../../v3/contentVersions/types'
import { globalQueryKeys } from '../queryKeys'

type UpdateLabelParams = { id: string; label: string | null }

/**
 * Updates a version's label with an optimistic cache write, rolling back on error.
 * The optimistic write targets the raw JSON:API document the query caches; the
 * hook's `select` re-derives the flattened rail from it.
 */
export const useUpdateContentVersionLabel = (contentUuid: string) => {
  const queryClient = useQueryClient()
  const queryKey = globalQueryKeys.contentVersions(contentUuid)

  return useMutation({
    mutationFn: ({ id, label }: UpdateLabelParams) =>
      contentVersions.updateLabel({ id, label }),
    onMutate: async ({ id, label }: UpdateLabelParams) => {
      await queryClient.cancelQueries(queryKey)
      const previous =
        queryClient.getQueryData<ReadContentVersionsDocument>(queryKey)

      queryClient.setQueryData<ReadContentVersionsDocument>(
        queryKey,
        (document) =>
          produce(document, (draft) => {
            const version = draft?.data.find((resource) => resource.id === id)
            if (version) version.attributes.label = label
          })
      )

      return { previous }
    },
    onError: (_error, _params, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(queryKey)
    },
  })
}
